import { type ReactElement, useEffect } from "react";

import {
  Row,
  FormField,
  Spinner,
  IconButton,
  RefreshIcon,
  Combobox,
} from "@hightouch/htui";
import { Link } from "src/router";
import * as Yup from "yup";

import { useGooglesheetsSourceListSheetsQuery } from "src/graphql";

import type {
  CustomQueryViewProps,
  CustomQueryFormProps,
} from "../custom-query";

export function QueryForm({
  source,
  query,
  onChange,
  setError,
}: Readonly<CustomQueryFormProps>): ReactElement<any, any> {
  const {
    data: sheets,
    isLoading: sheetsLoading,
    error: sheetsError,
    refetch: listSheets,
  } = useGooglesheetsSourceListSheetsQuery({ connectionId: String(source.id) });

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (sheetsError) {
      setError(sheetsError);
    }
  }, [sheetsError]);

  return (
    <FormField label="Sheet name" error={sheetsError?.message}>
      <Row gap={2}>
        <Combobox
          isLoading={sheetsLoading}
          options={
            sheets?.googlesheetsSourceListSheets?.map((name) => ({
              label: name ?? "",
              value: name,
            })) ?? []
          }
          placeholder="Select a sheet…"
          value={query?.name as any}
          onChange={(name) => onChange({ ...query, name })}
        />
        <IconButton
          aria-label="Refresh sheets"
          icon={RefreshIcon}
          isDisabled={sheetsLoading}
          variant="secondary"
          onClick={() => listSheets()}
        />
      </Row>
    </FormField>
  );
}

export function QueryView({
  source,
  query,
  setError,
}: Readonly<CustomQueryViewProps>): ReactElement<any, any> {
  const {
    data: sheets,
    isLoading: sheetsLoading,
    error: sheetsError,
  } = useGooglesheetsSourceListSheetsQuery({ connectionId: String(source.id) });

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    setError(sheetsError);
  }, [sheetsError]);

  const sheet = sheets?.googlesheetsSourceListSheets?.find(
    (name) => name === query.name,
  );
  const sheetName = sheet || `Unknown sheet ${query.name}`;

  return (
    <FormField label="Sheet">
      {sheetsLoading ? (
        <Spinner />
      ) : (
        <Link href={source.config.url}>{sheetName}</Link>
      )}
    </FormField>
  );
}

export const querySchema = Yup.object().shape({
  type: Yup.string().required().equals(["sheet"]),
  name: Yup.string().required(),
});
